// Permanent soft halo under the player ship: an additive white-cyan radial
// glow (~1.8× ship radius) with a subtle pulse, so the ship stays the
// unmistakable brightest object even when the swarm closes over it.
// Driven by sim time (world.tick + alpha), never the wall clock.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';

/** Soft radial glow (white core → transparent edge). Local twin of the tiny
 * helper in player-death-fx.ts — kept separate so that file stays untouched. */
let radialGlowTexture: THREE.CanvasTexture | null = null;
function makeRadialGlowTexture(): THREE.CanvasTexture {
  if (radialGlowTexture) return radialGlowTexture;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.5)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  radialGlowTexture = new THREE.CanvasTexture(canvas);
  return radialGlowTexture;
}

export class PlayerHalo {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;

  constructor() {
    this.material = new THREE.MeshBasicMaterial({
      map: makeRadialGlowTexture(),
      color: 0xa8ecff, // white-cyan aura around the warm-gold hull
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.material);
    this.mesh.frustumCulled = false;
  }

  /** Follow the ship (unwrapped camera-anchor position); `time` is sim
   * seconds so the pulse is identical across replays. */
  update(pos: Vec2, time: number, alive: boolean): void {
    this.mesh.visible = alive;
    if (!alive) return;
    // ~1.8× ship radius, breathing gently.
    const size = 3.3 * (1 + Math.sin(time * 2.4) * 0.07);
    this.mesh.scale.set(size, size, 1);
    this.material.opacity = 0.5 + Math.sin(time * 3.1) * 0.08;
    // Above the whole enemy layer (max enemy z ≈ 0.08) so the aura marks the
    // ship's spot even under a converged pile; still under the hull (z 0.1).
    this.mesh.position.set(pos.x, pos.y, 0.092);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
